import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ImageSlant from '../../common/image-slant';
import heroImage from '../../../assets/retail3.webp';
import bistroImage from '../../../assets/bistro.webp';
import farmshopImage from '../../../assets/farmshop.webp';

// Helper function to get the 3rd Sunday of a given month
const getThirdSunday = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const firstSunday = firstDay.getDay() === 0 ? 1 : (7 - firstDay.getDay() + 1);
  const thirdSunday = firstSunday + 14;
  return new Date(year, month, thirdSunday);
};

// Get all market dates and filter out past dates
const getUpcomingMarketDates = () => {
  const year = 2026;
  const months = [3, 4, 5, 6, 7, 8]; // April(3) to September(8) — 0-indexed months
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison
  
  const dates = months.map(month => {
    const eventDate = getThirdSunday(year, month);
    return {
      date: eventDate,
      formatted: eventDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    };
  });
  
  // Filter out past dates
  return dates.filter(item => {
    const eventDate = new Date(item.date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });
};

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
    padding: 0 1.5rem 2rem;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Card = styled.section`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 80px;
    height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};

  p {
    margin-bottom: 1.2rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
    }
  }
  
  strong {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: 1.05rem;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}15 0%, ${props => props.theme.colors.accent}25 100%);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1.5rem 0;
  
  p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${props => props.theme.colors.primary};
  }
  
  strong {
    color: ${props => props.theme.colors.accent};
  }
`;

const ContactButton = styled.a`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  text-align: center;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.875rem 1.5rem;
  }
`;

const CTABox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}15 0%, ${props => props.theme.colors.accent}25 100%);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 2rem;
  border-radius: 8px;
  margin: 2rem 0;
  text-align: center;
  
  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 1rem;
  }
  
  p {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.text};
    margin: 0 0 1.5rem;
    line-height: 1.6;
  }
`;

const CTAButton = styled.button`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 3rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 2rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  color: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  margin: 3rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  max-width: 200px;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const AntiquesMarket = () => {
  const navigate = useNavigate();
  const upcomingDates = getUpcomingMarketDates();

  return (
    <>
      <Helmet>
        <title>Vintage & Craft Market | Events at Belvoir Castle</title>
        <meta name="description" content="Join us for the Vintage & Craft Market every 3rd Sunday from April to September 2026. A monthly celebration of craftsmanship, character and timeless finds in the beautiful Retail Village." />
        <meta name="keywords" content="Belvoir Castle, antiques market, artisan market, craft market, retail village, Grantham events, monthly market, 2026" />
        <link rel="canonical" href="https://www.belvoircastle.com/events/antiques-artisan-market" />
        
        <meta property="og:title" content="Vintage & Craft Market | Events at Belvoir Castle" />
        <meta property="og:description" content="Join us for the Vintage & Craft Market every 3rd Sunday from April to September 2026. Free entry to this monthly celebration of craftsmanship and timeless finds." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/events/antiques-artisan-market" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vintage & Craft Market | Belvoir Castle" />
        <meta name="twitter:description" content="Monthly antiques and artisan market at Belvoir Castle. 3rd Sunday every month, April–September 2026. Free entry." />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": "Vintage & Craft Market",
            "description": "A monthly celebration of craftsmanship, character and timeless finds at the Belvoir Retail Village. Browse curated stalls featuring local artists, makers, antiques and collectables.",
            "startDate": "2026-04-19",
            "endDate": "2026-09-20",
            "eventSchedule": {
              "@type": "Schedule",
              "repeatFrequency": "P1M",
              "byDay": "http://schema.org/Sunday",
              "repeatCount": 6
            },
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "eventStatus": "https://schema.org/EventScheduled",
            "location": {
              "@type": "Place",
              "name": "Belvoir Retail Village",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belvoir Castle",
                "addressLocality": "Grantham",
                "postalCode": "NG32 1PA",
                "addressCountry": "GB"
              }
            },
            "organizer": {
              "@type": "Organization",
              "name": "Belvoir Castle",
              "url": "https://www.belvoircastle.com"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock",
              "url": "https://www.belvoircastle.com/events/antiques-artisan-market"
            }
          })}
        </script>
      </Helmet>

      <Page>
        <EventHero image={heroImage}>
          <HeroContent>
            <Badge>Monthly Market Event</Badge>
            <Title>Vintage & Craft Market</Title>
            <Strapline>3rd Sunday Every Month | April - September | 10:00 - 16:00 | Free Entry</Strapline>
          </HeroContent>
        </EventHero>

        <Content>
          <MainContent>
            <Card>
              <SectionTitle>About the Market</SectionTitle>
              <Body>
                <p>
                  Set within the charming surroundings of the Belvoir Retail Village, the Vintage & Craft Market 
                  is a monthly celebration of craftsmanship, character and timeless finds.
                </p>
                <p>
                  Taking place on the <strong>third Sunday of every month from April to September</strong>, the market brings together 
                  a carefully curated mix of local artists, makers and craftspeople, alongside antique and collectable sellers, 
                  offering everything from handmade homewares and gifts to vintage treasures and unique statement pieces.
                </p>
                <p>
                  Visitors can enjoy a relaxed day out browsing stalls set around the Retail Village, while also making the most 
                  of everything Belvoir Retail Village has to offer. From the welcoming bistro serving food and drink, to the 
                  independent retail outlets and the much-loved Belvoir Farm Shop.
                </p>
                <p>
                  Whether you're searching for something special, supporting local creatives, or simply enjoying a leisurely 
                  Sunday in beautiful surroundings, the Vintage & Craft Market is the perfect place to explore, 
                  discover and be inspired.
                </p>
              </Body>
            </Card>

            <CTABox>
              <h3>Stay Informed</h3>
              <p>Register your interest to receive updates about upcoming market dates, featured vendors, and exclusive market news.</p>
              <CTAButton onClick={() => navigate('/events/antiques-artisan-market/register')}>
                Register Your Interest
              </CTAButton>
            </CTABox>

            <Card>
              <SectionTitle>Interested in Trading with Us?</SectionTitle>
              <Body>
                <p>
                  We are always keen to hear from local artisans, artists, designers, and antique or collectable traders who 
                  would like to be part of the Vintage & Craft Market.
                </p>
                
                <p><strong>If you offer:</strong></p>
                <ul>
                  <li>Handmade arts and crafts</li>
                  <li>Artisan food or gift items</li>
                  <li>Antiques, vintage or collectables</li>
                  <li>Unique, high-quality products with character</li>
                </ul>
                
                <p>…we'd love to hear from you.</p>
                
                <HighlightBox>
                  <p>
                    To enquire about a stall, please get in touch via <strong>events@belvoircastle.com</strong> with a brief 
                    description of your products and any relevant images or links.
                  </p>
                </HighlightBox>
                
                <ContactButton href="mailto:events@belvoircastle.com?subject=Antiques%20Market%20Stall%20Enquiry">
                  Contact Us About Trading
                </ContactButton>
              </Body>
            </Card>
          </MainContent>

          <Sidebar>
            <Card>
              <SectionTitle>Event Details</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Dates</InfoLabel>
                  <InfoValue>3rd Sunday of every month, April - September 2026</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Time</InfoLabel>
                  <InfoValue>10:00 - 16:00</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Location</InfoLabel>
                  <InfoValue>Belvoir Retail Village</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Entry</InfoLabel>
                  <InfoValue>Free Entry</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Contact</InfoLabel>
                  <InfoValue>events@belvoircastle.com</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Card>

            <Card>
              <SectionTitle>2026 Market Dates</SectionTitle>
              <Body>
                {upcomingDates.length > 0 ? (
                  <ul>
                    {upcomingDates.map((item, index) => (
                      <li key={index}>{item.formatted}</li>
                    ))}
                  </ul>
                ) : (
                  <p>All 2026 market dates have passed. Check back soon for 2027 dates!</p>
                )}
              </Body>
            </Card>
          </Sidebar>
        </Content>

        <ImageSlant 
          image={bistroImage}
          title="Belvoir Bistro"
          description="Enjoy delicious food and refreshments at our welcoming bistro. The perfect complement to your market visit."
          buttonText="Learn More"
          buttonAction={() => navigate('/belvoir-bistro')}
          imagePosition="left"
        />

        <ImageSlant 
          image={farmshopImage}
          title="Belvoir Farm Shop"
          description="Visit our award-winning farm shop for the finest local produce, artisan foods, and Belvoir products."
          buttonText="Visit Farm Shop"
          buttonAction={() => navigate('/farm-shop')}
          imagePosition="right"
        />

        <BackButton onClick={() => navigate('/events')}>
          ← Back to Events
        </BackButton>

        <Footer />
      </Page>
    </>
  );
};

export default AntiquesMarket;
